import { levelById } from '../core/levels';
import type { BuildingKind, Dir } from '../core/types';
import { World, T_GROUND, T_ROCK } from '../core/world';
import { Renderer, type ViewState } from '../render/renderer';

/** A real factory, scripted, running behind the menus. */
export class Demo {
  private world: World;
  private renderer: Renderer;
  private raf = 0;
  private last = performance.now();
  private acc = 0;
  private t = 0;
  private view: ViewState = {
    tool: null, toolDir: 0, hover: null, place: null, dragPath: null, removeRect: null, copyRect: null, selectedId: null,
    showLabels: false, stamp: null, planMode: false, reducedMotion: false, showDebt: false,
  };
  private reduced: boolean;

  constructor(canvas: HTMLCanvasElement, reduced: boolean) {
    this.reduced = reduced;
    this.view.reducedMotion = reduced;
    this.renderer = new Renderer(canvas);
    this.world = this.build();
    this.renderer.cam.cx = 31;
    this.renderer.cam.cy = 21;
    this.renderer.cam.zoom = Math.max(22, Math.round(window.innerWidth / 48));
    // Warm up so belts are already full when the title appears.
    for (let i = 0; i < 30 * 40; i++) this.world.step();
    this.world.fx.length = 0;
    this.raf = requestAnimationFrame(this.frame);
    window.addEventListener('resize', this.onResize);
  }

  private onResize = () => this.renderer.resize();

  private build(): World {
    // A copy of level 3 with no goal or clock, so the demo never ends.
    const w = new World({ ...levelById('trust-but-verify')!, goal: { type: 'ship', count: 1e9 }, timeLimit: 0 });
    for (let i = 0; i < w.fog.length; i++) w.fog[i] = 1;
    for (let y = 10; y <= 34; y++) for (let x = 9; x <= 52; x++) if (w.terrain[y * w.w + x] === T_ROCK) w.terrain[y * w.w + x] = T_GROUND;
    w.credits = 9999;
    const P = (kind: BuildingKind, x: number, y: number, dir: Dir) => w.apply({ type: 'place', kind, x, y, dir }, 'system');
    const line = (x0: number, y0: number, x1: number, y1: number, dir: Dir) => {
      const items = [];
      const dx = Math.sign(x1 - x0);
      const dy = Math.sign(y1 - y0);
      for (let x = x0, y = y0; ; x += dx, y += dy) {
        items.push({ kind: 'belt' as const, x, y, dir });
        if (x === x1 && y === y1) break;
      }
      w.apply({ type: 'placeMany', items }, 'system');
    };
    P('indexer', 11, 13, 0);
    line(13, 13, 19, 13, 0);
    line(20, 13, 20, 20, 1);
    P('indexer', 11, 29, 0);
    line(13, 29, 19, 29, 0);
    line(20, 29, 20, 23, 3);
    P('specdesk', 20, 21, 0);
    line(22, 21, 29, 21, 0);
    P('forge', 30, 20, 0);
    line(32, 21, 32, 21, 0);
    P('bench', 33, 20, 0);
    line(35, 21, 49, 21, 0);
    line(33, 19, 33, 16, 3);
    P('postmortem', 33, 14, 0);
    P('checkpoint', 42, 15, 0);
    P('monitor', 30, 25, 0);
    return w;
  }

  private frame = (now: number) => {
    const dt = Math.min(0.1, (now - this.last) / 1000);
    this.last = now;
    this.t += dt;
    this.acc += dt;
    while (this.acc >= 1 / 30) {
      this.world.step();
      this.acc -= 1 / 30;
      if (this.world.outcome !== 'playing') this.world = this.build();
    }
    for (const f of this.world.fx) this.renderer.addFx(f, this.reduced);
    this.world.fx.length = 0;
    if (!this.reduced) {
      this.renderer.cam.cx = 31 + Math.sin(this.t * 0.05) * 6;
      this.renderer.cam.cy = 21 + Math.cos(this.t * 0.04) * 3;
    }
    this.renderer.render(this.world, this.view, this.acc * 30, dt);
    this.raf = requestAnimationFrame(this.frame);
  };

  stop() {
    cancelAnimationFrame(this.raf);
    window.removeEventListener('resize', this.onResize);
  }
}
